import type { 
  ExtendedWord, 
  LocationTag, 
  PhoneticCategory, 
  PhoneticGroup,
  SessionConfig 
} from '../types';
import { FREEMIUM } from '../constants';
// Import data from JSON files (Vite handles JSON imports natively)
import wordsData from './words.json';
import locationMetadataJson from './location-metadata.json';
import phoneticGroupMetadataJson from './phonetic-group-metadata.json';

// JSON shape interfaces (for cleaner type assertions on imported JSON)
interface LocationMetadataEntry {
  emoji: string;
  imageStyle: string;
}

interface PhoneticGroupMetadataEntry {
  emoji: string;
  categories: PhoneticCategory[];
}

/**
 * Extended word database loaded from words.json
 * Contains linguistic metadata for practice categories
 * Words marked as excluded in words.json are filtered out.
 */
const rawWordsData: ExtendedWord[] = (wordsData as ExtendedWord[]).filter(
  word => !word.excluded
);

/**
 * All extended words
 */
export const allExtendedWords: ExtendedWord[] = rawWordsData;

/**
 * Location tag metadata for UI display
 * Names use i18n keys (metadata:locations.{LocationTag})
 */
export const LOCATION_METADATA: Record<LocationTag, { emoji: string; hebrewName: string }> = 
  Object.fromEntries(
    Object.entries(locationMetadataJson as Record<string, LocationMetadataEntry>).map(([key, value]) => [
      key,
      { emoji: value.emoji, hebrewName: `metadata:locations.${key}` }
    ])
  ) as Record<LocationTag, { emoji: string; hebrewName: string }>;

/**
 * Phonetic group metadata for Sound Lab UI
 * Names use i18n keys (metadata:phonetics.groups.{group})
 */
export const PHONETIC_GROUP_METADATA: Record<PhoneticGroup, { 
  emoji: string; 
  hebrewName: string; 
  categories: PhoneticCategory[];
}> = Object.fromEntries(
  Object.entries(phoneticGroupMetadataJson as Record<string, PhoneticGroupMetadataEntry>).map(([key, value]) => [
    key,
    { 
      emoji: value.emoji, 
      hebrewName: `metadata:phonetics.groups.${key}`, 
      categories: value.categories,
    }
  ])
) as Record<PhoneticGroup, { emoji: string; hebrewName: string; categories: PhoneticCategory[] }>;

/**
 * Get letters for a phonetic group (for Sound Lab UI)
 */
export const getLettersByPhoneticGroup = (group: PhoneticGroup): string[] => {
  const categories = PHONETIC_GROUP_METADATA[group].categories;
  const letters = new Set<string>();
  
  rawWordsData.forEach(word => {
    if (categories.includes(word.phoneticCategory)) {
      letters.add(word.targetLetter);
    }
  });
  
  return Array.from(letters).sort();
};

/**
 * Get words by location tag
 */
export const getWordsByLocation = (location: LocationTag): ExtendedWord[] => {
  return rawWordsData.filter(word => word.locationTag === location);
};

/**
 * Get words by syllable count
 */
export const getWordsBySyllableCount = (count: number): ExtendedWord[] => {
  return rawWordsData.filter(word => word.syllableCount === count);
};

/**
 * Get words by target letter
 */
export const getWordsByTargetLetter = (letter: string): ExtendedWord[] => {
  return rawWordsData.filter(word => word.targetLetter === letter);
};

/**
 * Get words by phonetic category
 */
export const getWordsByPhoneticCategory = (category: PhoneticCategory): ExtendedWord[] => {
  return rawWordsData.filter(word => word.phoneticCategory === category);
};

/**
 * Get functional words (power words)
 */
export const getFunctionalWords = (): ExtendedWord[] => {
  return rawWordsData.filter(word => word.isFunctional);
};

/**
 * Get filtered words based on session config
 */
export const getFilteredExtendedWords = (config: SessionConfig): ExtendedWord[] => {
  return rawWordsData.filter(word => {
    // Filter by location
    if (config.locationTag && word.locationTag !== config.locationTag) {
      return false;
    }
    
    // Filter by target letter (for Sound Lab)
    if (config.targetLetter && word.targetLetter !== config.targetLetter) {
      return false;
    }
    
    // Filter by phonetic category
    if (config.phoneticCategory && word.phoneticCategory !== config.phoneticCategory) {
      return false;
    }
    
    // Filter by syllable count
    if (config.syllableCount && word.syllableCount !== config.syllableCount) {
      return false;
    }
    
    // Filter by functional (power words)
    if (config.isFunctional !== undefined && word.isFunctional !== config.isFunctional) {
      return false;
    }
    
    return true;
  });
};

/**
 * Get word by ID
 */
export const getWordById = (id: string): ExtendedWord | undefined => {
  return rawWordsData.find(word => word.id === id);
};

/**
 * Get all unique target letters
 */
export const getAllTargetLetters = (): string[] => {
  const letters = new Set<string>();
  rawWordsData.forEach(word => letters.add(word.targetLetter));
  return Array.from(letters).sort();
};

/**
 * Get all available locations that have words
 */
export const getAvailableLocations = (): LocationTag[] => {
  const locations = new Set<LocationTag>();
  rawWordsData.forEach(word => locations.add(word.locationTag));
  return Array.from(locations);
};

/**
 * Get statistics about the word database
 */
export const getWordStats = () => {
  const locationCounts: Record<string, number> = {};
  const syllableCounts: Record<number, number> = {};
  const letterCounts: Record<string, number> = {};
  let functionalCount = 0;

  rawWordsData.forEach(word => {
    locationCounts[word.locationTag] = (locationCounts[word.locationTag] || 0) + 1;
    syllableCounts[word.syllableCount] = (syllableCounts[word.syllableCount] || 0) + 1;
    letterCounts[word.targetLetter] = (letterCounts[word.targetLetter] || 0) + 1;
    if (word.isFunctional) functionalCount++;
  });

  return {
    totalWords: rawWordsData.length,
    locationCounts,
    syllableCounts,
    letterCounts,
    functionalCount
  };
};

// ============================================================================
// FREE TIER WORD SET
// ============================================================================

/** Phonetic categories included in the Tongue group (used for filler words) */
const TONGUE_CATEGORIES: PhoneticCategory[] = [
  'Alveolar (Front)',
  'Alveolar (Nasal)',
  'Alveolar (Liquid)',
  'Palatal',
];

/**
 * Build the set of word IDs available to free-tier users.
 *
 * Selection logic (speech-therapy aligned):
 * 1. All Lips group words (Bilabial Stop + Bilabial Nasal + Labiodental) — 62 words
 * 2. All functional / power words (with dedup against Lips) — adds 14 words → 76 total
 * 3. Fill remaining slots to reach FREE_WORD_LIMIT with Tongue group words,
 *    preferring lower syllable count (simpler words first)
 */
function buildFreeWordIds(): Set<string> {
  const ids = new Set<string>();

  // Step 1: All words from free phonetic categories (Lips group)
  const freeCategories = FREEMIUM.FREE_PHONETIC_CATEGORIES as readonly string[];
  for (const word of rawWordsData) {
    if (freeCategories.includes(word.phoneticCategory)) {
      ids.add(word.id);
    }
  }

  // Step 2: All functional words (power words) — many overlap with Lips
  for (const word of rawWordsData) {
    if (word.isFunctional) {
      ids.add(word.id);
    }
  }

  // Step 3: Fill remaining slots from Tongue group, preferring simpler words
  const remaining = FREEMIUM.FREE_WORD_LIMIT - ids.size;
  if (remaining > 0) {
    const tongueWords = rawWordsData
      .filter(w => TONGUE_CATEGORIES.includes(w.phoneticCategory) && !ids.has(w.id))
      .sort((a, b) => a.syllableCount - b.syllableCount || a.id.localeCompare(b.id, undefined, { numeric: true }));
    
    for (let i = 0; i < remaining && i < tongueWords.length; i++) {
      ids.add(tongueWords[i].id);
    }
  }

  return ids;
}

/** Set of word IDs available to free-tier users (computed once at module load) */
export const freeWordIds: Set<string> = buildFreeWordIds();

/** Get all words available to free-tier users */
export const getFreeWords = (): ExtendedWord[] => {
  return rawWordsData.filter(word => freeWordIds.has(word.id));
};

/** Check whether a word is available in the free tier */
export const isFreeWord = (id: string): boolean => {
  return freeWordIds.has(id);
};
